<template>
    <BaseCard theme="cream" radius="lgr" padding="none" class="mb-4 w-full min-w-0 md:w-full max-w-[600px] overflow-hidden">
        <div class="flex items-center gap-3 p-4">
            <span class="icon" aria-hidden="true"><i class="bi bi-file-text-fill"></i></span>
            <h3 class="nadia-chat-bold text-ink">{{ heading }}</h3>
        </div>
        <ul v-if="documents.length" class="divide-y divide-border border-t border-border">
            <li v-for="(document, index) in documents" :key="index" class="flex min-w-0 flex-col gap-1 p-4">
                <p class="body-small-semibold break-words text-ink">{{ displayName(document.name) }}</p>
                <p v-if="document.uploaded_at" class="scaffold-details-small text-ink-muted">
                    {{ t("chat.recentDocuments.sharedPrefix") }} <time :datetime="document.uploaded_at">{{ sharedDate(document.uploaded_at) }}</time>
                </p>
            </li>
        </ul>
        <p v-else class="scaffold-body-small px-4 pb-4 text-ink-muted">{{ description }}</p>
    </BaseCard>
</template>

<script setup lang="ts">
import { ACTION } from "~vue/chatActions";
import BaseCard from "~vue/components/BaseCard.vue";
import type { ChatMessageLine } from "~vue/types/chat";
import { formatDate } from "~vue/utils/threeSixty";
import { computed } from "vue";
import { useI18n } from "vue-i18n";

defineOptions({ inheritAttrs: false });
const { t } = useI18n();
const { action = null } = defineProps<{ action?: ChatMessageLine | null }>();
const result = computed(() => (action?.type === "action" && action.action_name === ACTION.QUERY_RECENT_DOCUMENT_UPLOADS ? action.action_params : null));
const documents = computed(() => {
    if (!result.value?.found) {
        return [];
    }
    return result.value.matched_documents ?? result.value.matched_file_names.map((name) => ({ name, uploaded_at: null }));
});
const heading = computed(() => {
    if (documents.value.length) {
        return documents.value.length === 1 ? t("chat.recentDocuments.foundOne") : t("chat.recentDocuments.foundMany");
    }
    return result.value?.found || !result.value ? t("chat.recentDocuments.searchComplete") : t("chat.recentDocuments.noneFound");
});
const description = computed(() => {
    if (result.value?.found || !result.value) {
        return t("chat.recentDocuments.detailsUnavailable");
    }
    return t("chat.recentDocuments.emptyDescription");
});

function displayName(name: string): string {
    if (/^pasted-content-.*\.txt$/.test(name)) {
        return t("chat.recentDocuments.pastedText");
    }
    return !name.trim() || /^upload-\d+$/.test(name) ? t("chat.recentDocuments.uploadedDocument") : name;
}

function sharedDate(uploadedAt: string): string {
    // Historical results stored only the UTC calendar date, so its local date cannot be recovered.
    if (/^\d{4}-\d{2}-\d{2}$/.test(uploadedAt)) {
        return `${formatDate(`${uploadedAt}T00:00:00`, "long")} (UTC)`;
    }
    return formatDate(uploadedAt, "long");
}
</script>

<style scoped>
@reference "../../../css/tailwind.css";

@layer components {
    /* Neutral file tile, same as the Reflections / role-play recap cards. */
    .icon {
        @apply flex size-9 shrink-0 items-center justify-center rounded-lg bg-surface-sunken text-ink-muted;
    }
}
</style>
